<template>
  <div v-if="isStripe" id="payment-element" />
</template>

<script setup lang="ts">
import {
  type Appearance,
  type Stripe,
  type StripeElements,
  type StripeElementsOptionsClientSecret,
  type StripePaymentElement,
  loadStripe,
} from '@stripe/stripe-js'
import { useCssVar } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'

import { MINIMUM_CHARGE_DOLLARS } from '@/orders'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'

const checkoutStore = useCheckoutStore()
const { pricing, client_secret } = storeToRefs(checkoutStore)

const settingStore = useSettingStore()
const { stripe_account, public_key } = storeToRefs(settingStore)

const GUEST_UI_QUERY = '.guest-ui'
const guestUIContainer = ref<HTMLDivElement | null>(null)
onMounted(() => {
  guestUIContainer.value = document.querySelector(GUEST_UI_QUERY)
})
const brandColor = useCssVar('--primary-theme-color')
const textColorOnBrand = useCssVar('--colors-button-label', guestUIContainer)
const inputBgColor = useCssVar('--colors-input', guestUIContainer)
const inputTextColor = useCssVar('--colors-fg-g-primary', guestUIContainer)
const labelTextColor = useCssVar('--colors-fg-disclaimer', guestUIContainer)
const primaryTextFontFamily = useCssVar('--fontFamily-sans', guestUIContainer)
const primaryTextFontFamilyUrl = useCssVar('--primary-text-font-family-url')
const borderColor = useCssVar('--colors-linear-g-primary', guestUIContainer)
const buttonStyle = { boxShadow: 'none', borderColor: borderColor.value, fontWeight: 'normal' }
const focusBorderColor = useCssVar('--g-fg-primary', null, { initialValue: 'black' })
const focusStyle = {
  boxShadow: `0 0 0 3px ${focusBorderColor.value}`,
  borderColor: 'transparent',
}
const elementsOptions = computed<StripeElementsOptionsClientSecret>(() => {
  return {
    clientSecret: client_secret.value!,
    fonts: primaryTextFontFamilyUrl.value ? [{ cssSrc: primaryTextFontFamilyUrl.value }] : [],
    appearance: {
      variables: {
        colorPrimary: brandColor.value,
        colorBackground: inputBgColor.value,
        colorText: inputTextColor.value,
        borderRadius: '4px',
        fontFamily: primaryTextFontFamily.value,
      },
      rules: {
        '.Tab': buttonStyle,
        '.Tab:hover': {
          backgroundColor: inputBgColor.value,
          borderColor: brandColor.value,
        },
        '.Tab:focus': focusStyle,
        '.Tab--selected': {
          backgroundColor: brandColor.value,
          color: textColorOnBrand.value,
          borderColor: brandColor.value,
        },
        '.Tab--selected:hover': {
          backgroundColor: brandColor.value,
          color: textColorOnBrand.value,
          borderColor: brandColor.value,
        },
        '.Tab--selected:focus': focusStyle,
        '.TabIcon--selected': { fill: textColorOnBrand.value },
        '.Input': buttonStyle,
        '.Input:focus': focusStyle,
        '.Label': { color: labelTextColor.value },
      },
    } as Appearance,
  }
})

const stripe = ref<Stripe | null>(null)
const elements = ref<StripeElements | null>(null)
const paymentElement = ref<StripePaymentElement | null>(null)
const isStripe = computed<boolean>(() => {
  return pricing.value.total >= MINIMUM_CHARGE_DOLLARS && public_key.value !== 'pk_test_xxx'
})

const isStripeFormComplete = ref<boolean>(false)
async function mountStripeForm() {
  await settingStore.loading
  // We want to avoid attempting to load stripe when we know we are running a test
  if (!isStripe.value) return

  stripe.value = await loadStripe(public_key.value, { stripeAccount: stripe_account.value })
  if (!stripe.value) return

  elements.value = stripe.value.elements(elementsOptions.value)
  if (!elements.value) return
  paymentElement.value = elements.value.create('payment', {
    layout: { type: 'tabs', defaultCollapsed: false },
  })
  paymentElement.value.on('change', event => {
    isStripeFormComplete.value = event.complete
  })
  paymentElement.value.mount('#payment-element')
}
function unmountStripeForm() {
  if (paymentElement.value) paymentElement.value.unmount()
  paymentElement.value = null
  elements.value = null
  stripe.value = null
  isStripeFormComplete.value = false
}

defineExpose({ mountStripeForm, unmountStripeForm, isStripeFormComplete, elements, stripe })
</script>
